import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ZODIAC_SIGNS } from '@yadviga/shared'
import { ChevronRight, FileDown, Sparkles } from 'lucide-react'
import { api, ApiError } from '../../api/client'
import { useSession } from '../../store/user.store'
import { Button } from '../../components/ui/Button'
import { LoadingDots } from '../../components/ui/LoadingDots'
import { ErrorState } from '../../components/ui/ErrorState'
import { MarkdownText } from '../../components/ui/Markdown'
import { PageHeader } from '../../components/ui/PageHeader'
import { SectionedMarkdown } from '../../components/ui/SectionedMarkdown'
import { ShareButton } from '../../components/ui/ShareButton'
import { useFetchNatalPdf, useSendNatalPdfToChat } from '../../hooks/use-natal-pdf'
import { toast } from 'react-hot-toast'
import { usePaywallStore } from '../../store/paywall.store'
import { BirthModal } from './birth-modal'

const signLabel = (id: string | null, lang: 'ru' | 'en') => {
  if (!id) return '—'
  const sign = ZODIAC_SIGNS.find((s) => s.id === id)
  if (!sign) return id
  return lang === 'en' ? sign.nameEn : sign.nameRu
}

export function AstroNatalRoute() {
  const { t, i18n } = useTranslation()
  const lang: 'ru' | 'en' = i18n.language === 'en' ? 'en' : 'ru'
  const navigate = useNavigate()
  const user = useSession((s) => s.user)
  const showPaywall = usePaywallStore((s) => s.show)
  const qc = useQueryClient()
  const [birthOpen, setBirthOpen] = useState(false)

  const teaserQuery = useQuery({
    queryKey: ['natal', 'teaser'],
    queryFn: () => api.natalTeaser(),
    enabled: Boolean(user?.birthDate),
    // Refetch on every mount of /natal — the user might be returning from
    // the bot notification and we want the freshest status (tier='full' or
    // still pending), not a stale cache. Cheap fetch, worth the freshness.
    refetchOnMount: 'always',
    staleTime: 0,
    // Poll every 8s while a background full-chart generation is in flight.
    // Once the server flips tier='full' (or generationPending=false), the
    // refetchInterval evaluates to false and polling stops.
    refetchInterval: (q) => (q.state.data?.generationPending ? 8000 : false),
  })

  const fullMutation = useMutation({
    mutationFn: () => api.natalFull(),
    onSuccess: (data) => {
      qc.setQueryData(['natal', 'teaser'], data)
      // No longer navigate — the full content now renders inline on this
      // same screen below the teaser.
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 402) {
        const detail = err.details
        showPaywall({
          reason: 'feature_locked',
          feature: (detail?.feature as string | undefined) ?? 'natal_full',
        })
      }
    },
  })

  const generationPending = teaserQuery.data?.generationPending ?? false

  // Full chart REQUIRES birth date + place. Birth time is OPTIONAL — if the
  // user doesn't know it, the backend falls back to noon (12:00) and skips
  // ascendant/houses; we show a small disclaimer so the user knows what
  // they're getting. Blocking the purchase entirely on missing time would
  // exclude the (very common) case of "I don't know my exact birth time".
  const hasFullBirthData = Boolean(user?.birthDate && user.hasBirthPlace)
  const fullChartIsApproximate = Boolean(user?.birthDate && user.hasBirthPlace && !user.hasBirthTime)

  if (!user?.birthDate) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <PageHeader title={t('natal.title')} backTo="/astro" />
        <p className="text-sage-700">{t('natal.no_birth')}</p>
        <Button size="lg" onClick={() => setBirthOpen(true)}>
          {t('natal.set_birth')}
        </Button>
        <BirthModal
          open={birthOpen}
          onClose={() => setBirthOpen(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['natal'] })}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader
        title={t('natal.title')}
        backTo="/astro"
        right={
          <button
            onClick={() => setBirthOpen(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-sage-100/70 px-3 py-1.5 text-xs text-sage-800 transition hover:bg-sage-200/80"
          >
            <img
              src="/icons/action-edit.png"
              alt=""
              aria-hidden="true"
              className="h-4 w-4"
              draggable={false}
            />
            <span>{t('profile.edit_birth')}</span>
          </button>
        }
      />

      {teaserQuery.isLoading && <LoadingDots compact />}
      {teaserQuery.isError && <ErrorState onRetry={() => teaserQuery.refetch()} />}

      {teaserQuery.data && (
        <>
          {/* Slim CTA banner — when full chart not yet unlocked we surface
              it right at the top so the user doesn't have to scroll. Once
              owned the banner is replaced by NatalFullInline below. */}
          {teaserQuery.data.tier !== 'full' && hasFullBirthData && !generationPending && (
            <button
              onClick={() => fullMutation.mutate()}
              disabled={fullMutation.isPending}
              className="group flex items-center gap-3 rounded-2xl bg-gradient-to-r from-copper/15 to-copper/5 px-4 py-3 ring-1 ring-copper/30 transition hover:from-copper/20 active:scale-[0.99] disabled:opacity-60"
            >
              <Sparkles className="h-5 w-5 shrink-0 text-copper" strokeWidth={1.6} aria-hidden />
              <span className="flex min-w-0 flex-1 flex-col items-start">
                <span className="font-serif text-sage-900">{t('natal.open_full')}</span>
                {fullChartIsApproximate && (
                  <span className="text-xs text-sage-600">{t('natal.no_time_disclaimer')}</span>
                )}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-copper" strokeWidth={2} aria-hidden />
            </button>
          )}

          {teaserQuery.data.tier !== 'full' && generationPending && (
            <div className="flex flex-col gap-2 rounded-2xl bg-sage-50 p-4 ring-1 ring-sage-200/60">
              <p className="text-sm text-sage-700">{t('natal.generating_async')}</p>
              <div className="flex items-center gap-2 text-xs text-sage-500">
                <span className="block h-1.5 w-1.5 animate-pulse rounded-full bg-sage-400" />
                <span className="block h-1.5 w-1.5 animate-pulse rounded-full bg-sage-400 [animation-delay:200ms]" />
                <span className="block h-1.5 w-1.5 animate-pulse rounded-full bg-sage-400 [animation-delay:400ms]" />
              </div>
            </div>
          )}

          {teaserQuery.data.tier !== 'full' && !hasFullBirthData && (
            <div className="flex flex-col gap-2 rounded-2xl bg-[#fbeadd] p-4 ring-1 ring-[#e6c4a4]/60">
              <p className="text-sm text-sage-700">{t('natal.full_needs_place')}</p>
              <Button
                size="md"
                onClick={() => setBirthOpen(true)}
                leftIcon={
                  <img
                    src="/icons/action-edit.png"
                    alt=""
                    aria-hidden="true"
                    className="h-4 w-4"
                    draggable={false}
                  />
                }
              >
                {t('natal.fill_data_to_continue')}
              </Button>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <BigStat
              label={t('natal.sun')}
              value={signLabel(teaserQuery.data.chart.sunSign, lang)}
              iconSrc="/icons/sign-sun.png"
            />
            <BigStat
              label={t('natal.moon')}
              value={signLabel(teaserQuery.data.chart.moonSign, lang)}
              iconSrc="/icons/sign-moon.png"
            />
            <BigStat
              label={t('natal.asc')}
              value={teaserQuery.data.chart.ascSign ? signLabel(teaserQuery.data.chart.ascSign, lang) : t('natal.asc_missing')}
              iconSrc="/icons/sign-asc.png"
            />
          </div>

          {teaserQuery.data.tier === 'full' && teaserQuery.data.fullInterpretation ? (
            <NatalFullInline
              fullText={teaserQuery.data.fullInterpretation}
              userGender={user?.gender ?? null}
            />
          ) : (
            <div className="rounded-3xl bg-white/70 p-5 ring-1 ring-sage-200/60">
              <MarkdownText>{teaserQuery.data.bigThreeInterpretation}</MarkdownText>
            </div>
          )}
        </>
      )}

      <BirthModal
        open={birthOpen}
        onClose={() => setBirthOpen(false)}
        onSaved={() => qc.invalidateQueries({ queryKey: ['natal'] })}
      />
    </div>
  )
}

function BigStat({ label, value, iconSrc }: { label: string; value: string; iconSrc: string }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-2xl bg-white/70 p-3 ring-1 ring-sage-200/60">
      <img
        src={iconSrc}
        alt=""
        aria-hidden="true"
        className="h-8 w-8 select-none"
        draggable={false}
      />
      <span className="text-[11px] uppercase tracking-wide text-sage-500">{label}</span>
      <span className="font-serif text-sm text-sage-800">{value}</span>
    </div>
  )
}

/**
 * Inline rendering of the full natal interpretation when the user already
 * owns it. PDF download + Share row at the top, then the long reading split
 * into per-section cards via SectionedMarkdown.
 */
function NatalFullInline({
  fullText,
  userGender,
}: {
  fullText: string
  userGender: 'female' | 'male' | 'other' | null
}) {
  const { t } = useTranslation()
  const sendToChat = useSendNatalPdfToChat()
  const fetchPdf = useFetchNatalPdf()
  const shareTextKey =
    userGender === 'female'
      ? 'natal.share_text_female'
      : userGender === 'male'
        ? 'natal.share_text_male'
        : 'natal.share_text_neutral'
  const handleSendToChat = () => {
    sendToChat.mutate(undefined, {
      onSuccess: (res) => {
        if (res.delivered) toast.success(t('natal.send_pdf_success'))
        else toast.error(t('natal.send_pdf_failed'))
      },
      onError: (err) => {
        toast.error((err as Error).message || t('natal.send_pdf_failed'))
      },
    })
  }
  return (
    <>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          size="lg"
          onClick={handleSendToChat}
          loading={sendToChat.isPending}
          leftIcon={<FileDown className="h-4 w-4" strokeWidth={1.8} aria-hidden />}
        >
          {t('natal.send_pdf_to_chat')}
        </Button>
        <ShareButton size="lg" text={t(shareTextKey)} getFile={fetchPdf} />
      </div>
      <SectionedMarkdown>{fullText}</SectionedMarkdown>
    </>
  )
}
